import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddIcon from "@mui/icons-material/Add";
import { DialogShell } from "@/shared/ui/DialogShell";
import { useTranscriptActions, useTranscriptDraft } from "../hooks/useTranscriptDraft";
import { formatSpeakerLabel, getSpeakerColorByLabel } from "./speakerColors";

interface SpeakerManagerDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Add / rename / delete speakers. A speaker that still has entries cannot be
 * deleted — the user must reassign or delete those entries first (the count and
 * disabled control make this explicit).
 */
export const SpeakerManagerDialog: React.FC<SpeakerManagerDialogProps> = ({
  open,
  onClose,
}) => {
  const theme = useTheme();
  const { speakers, speakerCounts } = useTranscriptDraft();
  const { addSpeaker, renameSpeaker, deleteSpeaker } = useTranscriptActions();

  const [editing, setEditing] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [newLabel, setNewLabel] = useState("");

  const startEdit = (label: string) => {
    setEditing(label);
    setEditValue(label);
  };
  const commitEdit = () => {
    const trimmed = editValue.trim();
    if (editing && trimmed && trimmed !== editing) renameSpeaker(editing, trimmed);
    setEditing(null);
  };

  const handleAdd = () => {
    const trimmed = newLabel.trim();
    if (!trimmed) return;
    addSpeaker(trimmed);
    setNewLabel("");
  };

  const duplicate =
    newLabel.trim().length > 0 && speakers.includes(newLabel.trim());

  return (
    <DialogShell
      open={open}
      onClose={onClose}
      size="xs"
      titleId="speaker-manager-dialog-title"
    >
      <DialogTitle id="speaker-manager-dialog-title" sx={{ fontWeight: 600, pr: 6 }}>
        Manage speakers
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Rename a speaker to relabel all of its entries. A speaker can only be
          deleted once it has no entries.
        </Typography>

        <List dense disablePadding>
          {speakers.map((label) => {
            const count = speakerCounts[label] ?? 0;
            const isEditing = editing === label;
            const chipColor = getSpeakerColorByLabel(
              label,
              speakers,
              theme.palette.mode,
            );
            return (
              <ListItem
                key={label}
                disableGutters
                sx={{ gap: 1, py: 0.75 }}
                secondaryAction={
                  !isEditing && (
                    <Box display="flex" gap={0.5}>
                      <Tooltip title="Rename">
                        <IconButton size="small" onClick={() => startEdit(label)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={
                          count > 0
                            ? `Reassign or delete this speaker's ${count} ${
                                count === 1 ? "entry" : "entries"
                              } first`
                            : "Delete speaker"
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={count > 0}
                            onClick={() => deleteSpeaker(label)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  )
                }
              >
                {isEditing ? (
                  <Stack direction="row" spacing={1} alignItems="center" flex={1}>
                    <TextField
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") commitEdit();
                        if (e.key === "Escape") setEditing(null);
                      }}
                      size="small"
                      autoFocus
                      fullWidth
                    />
                    <IconButton size="small" color="primary" onClick={commitEdit}>
                      <CheckIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => setEditing(null)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ) : (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      label={formatSpeakerLabel(label)}
                      size="small"
                      sx={{
                        backgroundColor: chipColor.soft,
                        color: chipColor.text,
                        border: `1px solid ${chipColor.border}`,
                        fontWeight: 500,
                        fontSize: "0.75rem",
                        height: 22,
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {count} {count === 1 ? "entry" : "entries"}
                    </Typography>
                  </Box>
                )}
              </ListItem>
            );
          })}
        </List>

        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 2 }}>
          <TextField
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAdd();
            }}
            size="small"
            fullWidth
            placeholder="New speaker name"
            error={duplicate}
            helperText={duplicate ? "That speaker already exists" : " "}
          />
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            disabled={!newLabel.trim() || duplicate}
            sx={{ textTransform: "none", mt: 0.25 }}
          >
            Add
          </Button>
        </Stack>
      </DialogContent>
    </DialogShell>
  );
};
